import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useGameStore from '@store/useGameStore';
import useAuthStore from '@store/useAuthStore';
import { GameStatus, PlayerStatusInGame } from '@utils/enums';
import { finalizePlanningApi, finalizeDeploymentApi, continueToNextTurnApi } from '@api/gameApi';
import { EMPTY_ARRAY } from '@utils/constants';
import feedback from '@/components/ui/general/feedback/feedback';
import './GameStatusButtonPanel.css';

/**
 * Dynamic Game Status Button Panel
 * Shows appropriate action buttons based on current game phase
 * Centralizes all game action buttons in one place
 */
export default function GameStatusButtonPanel() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Get data from stores
  const game = useGameStore((state) => state.game);
  const user = useAuthStore((state) => state.user);
  
  // Derive current player and status
  const players = useMemo(() => game?.players || EMPTY_ARRAY, [game]);
  const currentPlayer = players.find((p) => p.user_id === user?.id) ?? null;
  const playerStatus = currentPlayer?.pivot?.status_in_game;
  const gameStatus = game?.status;
  
  // Get units using the getter with getState() to avoid subscription loops
  const hasDeployedUnits = useMemo(() => {
    if (!currentPlayer) return false;
    const units = useGameStore.getState().getCurrentUnits();
    const deployedUnits = units.filter(u => u.player_id === currentPlayer.id);
    return deployedUnits.length > 0;
  }, [currentPlayer, gameStatus]); // Re-calculate when player or game status changes
  
  // Check if player has planned actions (for planning phase)
  const hasPlannedActions = useMemo(() => {
    if (!currentPlayer) return false;
    // Only check planned actions during planning phase
    if (gameStatus !== GameStatus.Plan) return false;
    
    const plannedActions = useGameStore.getState().getPlannedActions();
    const playerActions = plannedActions.filter(a => a.player_id === currentPlayer.id);
    return playerActions.length > 0;
  }, [currentPlayer, gameStatus]); // Re-calculate when player or game status changes
  
  // Button handlers
  const handleFinalizeDeployment = async () => {
    if (!game || isLoading) return;
    
    setIsLoading(true);
    try {
      await finalizeDeploymentApi(game.id);
    } catch (error) {
      console.error("[GameStatusButtonPanel] Error finalizing deployment:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFinalizePlanning = async () => {
    if (!game || isLoading) return;
    
    setIsLoading(true);
    try {
      await finalizePlanningApi(game.id);
    } catch (error) {
      console.error("[GameStatusButtonPanel] Error finalizing planning:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleContinueAfterResolution = async () => {
    if (!game?.id) return;

    setIsLoading(true);
    try {
      const response = await continueToNextTurnApi(game.id);

      // Check if we're waiting for other players
      const userMessage = response?.userMessage || '';
      if (userMessage.includes('Esperando')) {
        feedback.info(userMessage);
      }
      // Game will be updated through the API interceptor
    } catch (error) {
      console.error('[GameStatusButtonPanel] Failed to continue to next turn:', error);
      feedback.error('Error al continuar al siguiente turno');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Determine button configuration based on game status
  const getButtonConfig = () => {
    if (!game || !currentPlayer) return null;
    
    switch (gameStatus) {
      case GameStatus.Deploy:
        return {
          text: playerStatus === PlayerStatusInGame.Deployed 
            ? 'Waiting for other players...' 
            : hasDeployedUnits 
              ? 'Finalize Deployment'
              : 'Deploy units first',
          onClick: handleFinalizeDeployment,
          disabled: isLoading || 
                   playerStatus === PlayerStatusInGame.Deployed || 
                   !hasDeployedUnits,
          variant: 'primary'
        };
        
      case GameStatus.Plan:
        return {
          text: playerStatus === PlayerStatusInGame.Planned
            ? 'Waiting for other players...'
            : 'Finalize Planning',
          onClick: handleFinalizePlanning,
          disabled: isLoading || playerStatus === PlayerStatusInGame.Planned,
          variant: 'primary'
        };
        
      case GameStatus.Resolved:
        return {
          text: 'Continue to Next Turn',
          onClick: handleContinueAfterResolution,
          disabled: isLoading,
          variant: 'success'
        };
        
      case GameStatus.Resolving:
        return {
          text: 'Resolving actions...',
          onClick: () => {},
          disabled: true,
          variant: 'secondary'
        };
        
      case GameStatus.Finish:
        return {
          text: 'Return to Dashboard',
          onClick: () => navigate('/dashboard'),
          disabled: false,
          variant: 'primary'
        };
        
      default:
        return null;
    }
  };
  
  const buttonConfig = getButtonConfig();
  
  // Don't render if no button needed for current phase
  if (!buttonConfig) return null;
  
  return (
    <div className="game-status-button-panel">
      <button
        className={`btn-enhanced btn-spaced btn-${buttonConfig.variant}`}
        onClick={buttonConfig.onClick}
        disabled={buttonConfig.disabled}
      >
        {isLoading ? (
          <span className="loading-spinner">Processing...</span>
        ) : (
          buttonConfig.text
        )}
      </button>
      
      {/* Optional: Add helper text based on status */}
      {gameStatus === GameStatus.Deploy && !hasDeployedUnits && (
        <p className="helper-text">
          Select units from the panel above and place them on spawn beacons
        </p>
      )}
      
      {gameStatus === GameStatus.Plan && !hasPlannedActions && (
        <p className="helper-text">
          Select your units and plan their actions for this turn
        </p>
      )}
    </div>
  );
}